/**
 * Quiet hours — per-user "don't buzz me right now" windows.
 *
 * ─── What quiet hours do and don't do ───────────────────────────────────────
 *
 * They suppress the PUSH NOTIFICATION. They never delay, hold, or reorder the
 * message itself. A message sent at 11:47pm lands in the thread at 11:47pm and
 * is visible the moment the other parent opens the app; they simply are not
 * woken up for it.
 *
 * This distinction matters more here than in most products. These threads get
 * exported for mediation and custody proceedings, so a delivery timestamp that
 * doesn't match when the message was actually sent would put a false record in
 * front of a court. A parent who genuinely wants the message to ARRIVE later
 * schedules it explicitly (see scheduleMessage) — that path creates the message
 * at delivery time, so its timestamp stays truthful too.
 *
 * ─── Window semantics ───────────────────────────────────────────────────────
 *
 * Hours are integers 0-23 in the RECIPIENT's local timezone. The window is
 * half-open: [startHour, endHour). A window that wraps midnight (21 → 8) is the
 * normal case, so the containment check handles wrapping explicitly rather than
 * assuming start < end.
 *
 * start === end means a zero-length window, which reads as "never quiet". The
 * alternative (always quiet) would silently disable every notification a user
 * has, from what looks like a harmless setting.
 *
 * The mirrored Cloud Function implementation lives in functions/quietHours.js —
 * that file cannot import this one (functions is CommonJS and not a workspace
 * package). The two must stay in sync; the tests in
 * packages/shared/src/utils/__tests__/quietHours.test.js pin the behavior.
 */

import { DEFAULT_QUIET_HOURS } from './constants';

/**
 * Normalize a stored quiet-hours object, filling gaps with defaults.
 * Non-integer or out-of-range hours fall back rather than throwing — a bad
 * value should degrade to "not quiet", never to a crash or a silent blackout.
 *
 * @param {Object|null|undefined} stored - users/{uid}.quietHours
 * @returns {{enabled: boolean, startHour: number, endHour: number}}
 */
export function normalizeQuietHours(stored) {
  const validHour = (value, fallback) =>
    Number.isInteger(value) && value >= 0 && value <= 23 ? value : fallback;

  return {
    enabled: stored?.enabled === true,
    startHour: validHour(stored?.startHour, DEFAULT_QUIET_HOURS.startHour),
    endHour: validHour(stored?.endHour, DEFAULT_QUIET_HOURS.endHour),
  };
}

/**
 * Is `hour` inside the window [startHour, endHour)?
 * Handles windows that wrap past midnight.
 *
 * @param {number} hour - 0-23
 * @returns {boolean}
 */
export function hourInWindow(hour, startHour, endHour) {
  if (startHour === endHour) return false; // zero-length window: never quiet
  if (startHour < endHour) return hour >= startHour && hour < endHour;
  return hour >= startHour || hour < endHour; // wraps midnight
}

/**
 * Current hour (0-23) for an IANA timezone. Falls back to UTC on a missing or
 * invalid zone, matching how the handoff reminder resolves user clocks.
 *
 * @param {string} timezone - IANA zone, e.g. "America/New_York"
 * @param {Date} [now]
 * @returns {number}
 */
export function localHour(timezone, now = new Date()) {
  try {
    return Number(
      new Intl.DateTimeFormat('en-US', {
        timeZone: timezone || 'Etc/UTC',
        hour: 'numeric',
        hour12: false,
      }).format(now)
    ) % 24;
  } catch {
    return now.getUTCHours();
  }
}

/**
 * Should a push to this user be suppressed right now?
 *
 * @param {Object|null} quietHours - the user's stored quietHours
 * @param {string} timezone - the user's IANA timezone
 * @param {Date} [now]
 * @returns {boolean}
 */
export function isQuietNow(quietHours, timezone, now = new Date()) {
  const { enabled, startHour, endHour } = normalizeQuietHours(quietHours);
  if (!enabled) return false;
  return hourInWindow(localHour(timezone, now), startHour, endHour);
}

/**
 * The next moment quiet hours lift, as a Date in absolute time.
 *
 * Used by the composer to offer "Send at 8:00 AM" when the co-parent is
 * currently in their quiet window. Computed by walking forward hour by hour
 * from now (at most 24 steps) rather than by constructing a date in the target
 * timezone — timezone-aware date construction without a tz library is where
 * DST bugs live, and stepping forward is correct across DST shifts by
 * construction.
 *
 * @param {Object|null} quietHours
 * @param {string} timezone
 * @param {Date} [now]
 * @returns {Date|null} null when quiet hours are off or not currently active
 */
export function quietHoursEndsAt(quietHours, timezone, now = new Date()) {
  const { enabled, startHour, endHour } = normalizeQuietHours(quietHours);
  if (!enabled) return null;
  if (!hourInWindow(localHour(timezone, now), startHour, endHour)) return null;

  const cursor = new Date(now.getTime());
  // Advance to the top of the next hour, then step until the window lifts.
  cursor.setMinutes(0, 0, 0);
  cursor.setHours(cursor.getHours() + 1);

  for (let step = 0; step < 24; step += 1) {
    if (!hourInWindow(localHour(timezone, cursor), startHour, endHour)) return cursor;
    cursor.setHours(cursor.getHours() + 1);
  }
  return null;
}

/** Human label for a window, e.g. "9:00 PM – 8:00 AM". */
export function formatQuietWindow(quietHours) {
  const { startHour, endHour } = normalizeQuietHours(quietHours);
  const label = (hour) => {
    const suffix = hour < 12 ? 'AM' : 'PM';
    const display = hour % 12 === 0 ? 12 : hour % 12;
    return `${display}:00 ${suffix}`;
  };
  return `${label(startHour)} – ${label(endHour)}`;
}
